import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { getListJalurJenis, getListJenisUkt } from "@/services/getData";
import { JalurJenis, JenisUkt } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { LuListFilter } from "react-icons/lu";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function FormFilter({ closeModal }: { closeModal: () => void }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const idsRefJalurJenis = searchParams.get("ids_ref_jalur_jenis");
  const idsJenisUkt = searchParams.get("ids_jenis_ukt");

  const [selectedFilter, setSelectedFilter] = useState("JENIS_UKT");
  const [filter, setFilter] = useState<{
    jalur_jenis: string[];
    jenis_ukt: string[];
  }>({
    jalur_jenis: idsRefJalurJenis?.split("-") || [],
    jenis_ukt: idsJenisUkt?.split("-") || [],
  });

  const { data: listJenisUkt, isPending: isPendingListJenisUkt } = useQuery({
    queryKey: ["listJenisUkt"],
    queryFn: getListJenisUkt,
  });

  const { data: listJalurJenis, isPending: isPendingListJalurJenis } = useQuery(
    {
      queryKey: ["listJalurJenis"],
      queryFn: getListJalurJenis,
    },
  );

  const handleApplyFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (filter.jalur_jenis.length > 0) {
      searchParams.set("ids_ref_jalur_jenis", filter.jalur_jenis.join("-"));
    } else {
      searchParams.delete("ids_ref_jalur_jenis");
    }

    if (filter.jenis_ukt.length > 0) {
      searchParams.set("ids_jenis_ukt", filter.jenis_ukt.join("-"));
    } else {
      searchParams.delete("ids_jenis_ukt");
    }

    navigate(`/admin/data-ukt-pendaftar-lulus?${searchParams.toString()}`);

    closeModal();
  };

  return (
    <form
      onSubmit={handleApplyFilter}
      className="pb-20 relative flex flex-col gap-y-3"
    >
      <FormHeader title="Filter Data Pendaftaran" icon={<LuListFilter />} />

      <section className="p-3 relative border rounded-xl">
        <div
          className="flex justify-between items-center"
          onClick={() =>
            setSelectedFilter((prev) =>
              prev === "JENIS_UKT" ? "" : "JENIS_UKT",
            )
          }
        >
          <span className="font-bold">Jenis UKT</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "JENIS_UKT" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "JENIS_UKT" &&
          (isPendingListJenisUkt ? (
            "Loading..."
          ) : (
            <div className="mt-3 grid grid-cols-3 gap-5">
              {listJenisUkt?.map((jenisUkt: JenisUkt) => {
                const isChecked = filter.jenis_ukt.includes(
                  String(jenisUkt.id_ref_jenis_ukt),
                );

                return (
                  <div key={jenisUkt.id_ref_jenis_ukt} className="flex gap-3">
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        setFilter((prev) => {
                          const updatedJenisUkt = [...prev.jenis_ukt];

                          if (checked) {
                            if (
                              !updatedJenisUkt.includes(
                                String(jenisUkt.id_ref_jenis_ukt),
                              )
                            ) {
                              updatedJenisUkt.push(
                                String(jenisUkt.id_ref_jenis_ukt),
                              );
                            }
                          } else {
                            const index = updatedJenisUkt.indexOf(
                              String(jenisUkt.id_ref_jenis_ukt),
                            );
                            if (index > -1) {
                              updatedJenisUkt.splice(index, 1);
                            }
                          }

                          return {
                            ...prev,
                            jenis_ukt: updatedJenisUkt,
                          };
                        });
                      }}
                    />
                    <Label>{jenisUkt.nama_jenis_ukt}</Label>
                  </div>
                );
              })}
            </div>
          ))}
      </section>

      <section className="p-3 relative border rounded-xl">
        <div
          className="flex justify-between items-center"
          onClick={() =>
            setSelectedFilter((prev) => (prev === "JALUR" ? "" : "JALUR"))
          }
        >
          <span className="font-bold">Jalur</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "JALUR" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "JALUR" &&
          (isPendingListJalurJenis ? (
            "Loading..."
          ) : (
            <div className="mt-3 grid grid-cols-3 gap-5">
              {listJalurJenis?.map((jalurJenis: JalurJenis) => {
                const isChecked = filter.jalur_jenis.includes(
                  String(jalurJenis.id_ref_jalur_jenis),
                );

                return (
                  <div
                    key={jalurJenis.id_ref_jalur_jenis}
                    className="flex gap-3"
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        setFilter((prev) => {
                          const updatedJalurJenis = [...prev.jalur_jenis];

                          if (checked) {
                            if (
                              !updatedJalurJenis.includes(
                                String(jalurJenis.id_ref_jalur_jenis),
                              )
                            ) {
                              updatedJalurJenis.push(
                                String(jalurJenis.id_ref_jalur_jenis),
                              );
                            }
                          } else {
                            const index = updatedJalurJenis.indexOf(
                              String(jalurJenis.id_ref_jalur_jenis),
                            );
                            if (index > -1) {
                              updatedJalurJenis.splice(index, 1);
                            }
                          }

                          return {
                            ...prev,
                            jalur_jenis: updatedJalurJenis,
                          };
                        });
                      }}
                    />
                    <Label>{jalurJenis.nama_jalur_jenis}</Label>
                  </div>
                );
              })}
            </div>
          ))}
      </section>

      <div className="absolute bottom-3 left-3 right-3">
        <div className="flex justify-between">
          <div className="flex gap-3">
            <Button
              onClick={() => closeModal()}
              type="button"
              variant={"outline"}
            >
              Batal
            </Button>
            <Button>Terapkan Filter</Button>
          </div>
          <Button
            type="button"
            variant={"outline"}
            onClick={() => {
              setFilter((prev) => ({
                ...prev,
                jalur_jenis: [],
                jenis_ukt: [],
              }));
              navigate("/admin/data-ukt-pendaftar-lulus");
            }}
          >
            Reset
          </Button>
        </div>
      </div>
    </form>
  );
}
